import type { Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import { obliqueCorners } from './lotFootprint';
import { lotFrame } from './lotFrame';
import type { PartyWalls, Stretch } from './partyWalls';
import type { Lot } from './types';

/**
 * PLAN Houses, deep plots (2026-10-06): the rows read on the painting along the Damrak, the
 * Nieuwendijk and by the Oude Kerk give each plot one house, up to 32 m deep under one roof. The
 * 1538 painting draws those blocks with two roofs to a plot, and the city's houses stood 5.6 to
 * 12 m deep (frontDepth.ts). A house deeper than `deepest` is therefore built as a front house on
 * `front` of the plot, no less than `least` and no more than `most` m deep, and a back house on the
 * rest, `slot` m behind it and each storey `lower` m lower. Where the plot is divided is an
 * estimate; the plot, its place and its authored storeys and roof are as they were. The rows in
 * `whole` were authored as single long buildings and stay so.
 */
export const DEEP = {
  deepest: 20,
  front: 0.45,
  least: 10,
  most: 14,
  slot: 0.4,
  lower: 0.25,
  whole: ['hoge-huis', 'raam-', 'claverblat'],
} as const;

const STOREY_HEIGHT = 3.05;

interface Span {
  readonly from: number;
  readonly to: number;
}

function isWhole(lot: Lot): boolean {
  const row = lot.reconstruction?.row;
  return row !== undefined && DEEP.whole.some((prefix) => row.startsWith(prefix));
}

/* The part of a plot between two depths behind its front, on the plot's own lines. */
function part(lot: Lot, span: Span): Lot {
  const { frontMid, facing, right, width, shear = 0 } = lotFrame(lot);
  const depth = span.to - span.from;
  const front: Vec2 = [
    frontMid[0] - facing[0] * span.from + right[0] * shear * span.from,
    frontMid[1] - facing[1] * span.from + right[1] * shear * span.from,
  ];
  const obb = {
    center: [
      front[0] - (facing[0] * depth) / 2 + (right[0] * shear * depth) / 2,
      front[1] - (facing[1] * depth) / 2 + (right[1] * shear * depth) / 2,
    ] as Vec2,
    axisU: right,
    halfU: width / 2 + (Math.abs(shear) * depth) / 2,
    halfV: depth / 2,
  };
  return {
    ...lot,
    obb,
    depth,
    facing,
    corners: lot.plot ? obliqueCorners(front, facing, width, depth, shear) : obbCorners(obb),
    ...(lot.plot ? { plot: { frontMid: front, shear } } : {}),
  };
}

function frontOf(lot: Lot): Lot {
  if (!lot.reconstruction) return lot;
  const reconstruction = { ...lot.reconstruction };
  delete reconstruction.waterfront;
  return { ...lot, reconstruction };
}

function backOf(lot: Lot): Lot {
  const back: Lot = { ...lot, kind: 'backhouse' };
  if (!lot.reconstruction) return back;
  const { params } = lot.reconstruction;
  return {
    ...back,
    reconstruction: {
      ...lot.reconstruction,
      params: {
        ...params,
        frontGable: 'plain',
        storeyHeight: (params.storeyHeight ?? STOREY_HEIGHT) - DEEP.lower,
      },
    },
  };
}

/** The houses a plot's house is built as: itself, or a front house and a back house on a deep plot. */
export function housesOnPlot(lot: Lot): readonly Lot[] {
  const { depth } = lotFrame(lot);
  if (lot.kind !== 'house' || depth <= DEEP.deepest || isWhole(lot)) return [lot];
  const front = Math.min(DEEP.most, Math.max(DEEP.least, depth * DEEP.front));
  return [
    frontOf(part(lot, { from: 0, to: front })),
    backOf(part(lot, { from: front + DEEP.slot, to: depth })),
  ];
}

/* A stretch measured from the plot's back corner, as it lies along a part of the plot. */
function along(stretch: Stretch, span: Span, depth: number): Stretch | null {
  const back = depth - span.to;
  const from = Math.max(0, stretch[0] - back);
  const to = Math.min(span.to - span.from, stretch[1] - back);
  return to > from ? [from, to] : null;
}

/** The neighbours' walls along one house of a deep plot, from those along the whole plot. */
export function partyOnPart(party: PartyWalls, span: Span, depth: number): PartyWalls {
  const on = (stretches: readonly Stretch[]) =>
    stretches
      .map((stretch) => along(stretch, span, depth))
      .filter((stretch): stretch is Stretch => stretch !== null);
  return { ...party, covered: { left: on(party.covered.left), right: on(party.covered.right) } };
}
